"use client";

import { Box, Stack, Typography, IconButton } from "@mui/material";
import { PlayArrow } from "@mui/icons-material";
import { mockReview } from "@/data/review_mock_data";
import { ReviewType } from "@/types/schema";
import { useState, useRef, useEffect } from "react";

const VideoReviewCard = ({ videoUrl }: { videoUrl: string }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  // Control video playback based on isPlaying state
  useEffect(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [isPlaying]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: "460px", md: "400px" },
        borderRadius: "16px",
        overflow: "hidden",
        bgcolor: "#070C0F",
        cursor: "pointer",
      }}
      onClick={handlePlay}
    >
      {!isPlaying ? (
        <>
          {/* Video Thumbnail */}
          <Box
            component="video"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
            muted
            preload="metadata"
          >
            <source src={videoUrl} type="video/mp4" />
          </Box>

          {/* Play Button Overlay */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(0, 0, 0, 0.3)",
            }}
          >
            <IconButton
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.9)",
                color: "#070C0F",
                width: 64,
                height: 64,
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 1)",
                },
              }}
            >
              <PlayArrow sx={{ fontSize: 32 }} />
            </IconButton>
          </Box>
        </>
      ) : (
        <Box
          component="video"
          ref={videoRef}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
          controls
        >
          <source src={videoUrl} type="video/mp4" />
        </Box>
      )}
    </Box>
  );
};

export const RealCustomersSection = () => {
  // Filter video reviews for this section
  const videoReviews = mockReview.filter(review =>
    review.reviewType === ReviewType.VIDEO
  );

  return (
    <Box
      sx={{
        px: { xs: 4, md: 0 },
        py: { xs: 15, md: 25 },
        position: "relative",
        bgcolor: "#070C0F",
        overflow: "hidden",
      }}
    >
      {/* Background Gradient Circle */}
      <Box
        sx={{
          position: "absolute",
          width: { xs: "504px", md: "844px" },
          height: { xs: "388px", md: "180px" },
          left: { xs: "-64px", md: "298px" },
          top: { xs: "1712px", md: "586px" },
          background: "linear-gradient(135deg, #970400 0%, #CE9852 100%)",
          borderRadius: "50%",
          zIndex: 0,
        }}
      />

      {/* Blur Overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: "rgba(11, 17, 20, 0.01)",
          backdropFilter: "blur(250px)",
          zIndex: 1,
        }}
      />

      {/* Content */}
      <Stack
        alignItems="center"
        gap={{ xs: "48px", md: "48px" }}
        sx={{
          position: "relative",
          zIndex: 2,
          maxWidth: "100%",
        }}
      >
        {/* Title */}
        <Typography
          variant="h2"
          sx={{
            background: "linear-gradient(90deg, #CD7858 0%, #FFD969 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontSize: { xs: "24px", md: "32px" },
            lineHeight: { xs: "32px", md: "38px" },
            fontWeight: 600,
            textAlign: "center",
            fontFamily: "var(--font-noto)",
          }}
        >
          การันตีความแซ่บ
        </Typography>

        {/* Video Grid */}
        <Box
          sx={{
            width: '100%',
            display: { xs: 'block', md: 'flex' },
            justifyContent: 'center',
          }}
        >
          {/* Desktop Layout - 2x2 Grid in Row */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              px: 30,
              overflowX: "auto",
              "&::-webkit-scrollbar": {
                display: "none"
              },
              scrollbarWidth: "none"
            }}
          >
            <Stack
              direction="row"
              gap={5}
              sx={{
                minWidth: 'max-content',
              }}
            >
              {videoReviews.map((review, index) => (
                <Box key={`desktop-${index}`} sx={{ flex: '0 0 auto', width: '285px' }}>
                  <VideoReviewCard videoUrl={review.review} />
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Mobile Layout - 2x2 Grid in Column */}
          <Stack
            gap={5}
            sx={{
              display: { xs: "flex", md: "none" },
            }}
          >
            {videoReviews.map((review, index) => (
              <VideoReviewCard key={`mobile-${index}`} videoUrl={review.review} />
            ))}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};